import * as React from 'react'

import { CopyIcon, PencilIcon, RotateCwIcon, Trash2Icon } from 'lucide-react'
import { observer } from 'mobx-react'
import { createPortal } from 'react-dom'

import { Button } from '~/client/src/shared/components/Button/Button'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import MarkupEditorStore, { TOOLBAR_GAP } from './MarkupEditorStore'
import { RESIZE_HANDLE_CLASS, ROTATE_HANDLE_CLASS } from './markupHandleStyle'
import MarkupTextBox from './MarkupTextBox'

interface IProps {
  store: MarkupEditorStore
}

// Selection chrome shared by both (desktop/mobile) shells; reads everything off the store.
const MarkupSelectionChrome: React.FC<IProps> = observer(({ store }) => {
  // Touch places a text box on pointerdown; the compatibility click that follows must
  // be swallowed so it doesn't place a second box (see the text place-overlay below).
  const placedOnTouchRef = React.useRef(false)
  // Text boxes portal into the map container so they sit beneath the map chrome,
  // matching the read-only preview (toolbars stay in the z-10 overlay, above it).
  const renderTextBoxes = (): React.ReactNode => {
    // Only the selected, editing or lingering box is DOM; the rest are GL. None: skip the
    // viewport-tracking textPose read (it would reconcile this chrome every camera frame).
    const domIds = store.textSwap.domTextIds
    if (!domIds.length) return null
    const { scale, pitch, bearing, perspective } = store.textPose
    // Active tool wins: only the Select tool (and not pin-edit) lets text catch
    // pointer events; a drawing tool must draw straight through the boxes.
    const interactive =
      store.activeTool === 'select' && !store.isLocationEditActive
    const layer = (
      <div
        className="tw:pointer-events-none tw:absolute tw:inset-0"
        // Match the map camera so tilted boxes recede toward the same vanishing
        // point as the GL geometry (origin = map centre, the default).
        style={perspective ? { perspective: `${perspective}px` } : undefined}
      >
        {store.textStore.boxes.map(box => {
          if (!domIds.includes(box.id)) return null
          const screen = store.textMapScreens.get(box.id)
          if (!screen) return null
          return (
            <MarkupTextBox
              key={box.id}
              box={box}
              screen={screen}
              scale={scale}
              pitch={pitch}
              bearing={bearing}
              isSelected={store.textStore.selectedId === box.id}
              isEditing={store.textStore.editingId === box.id}
              interactive={interactive}
              map={store.map}
              onTap={(x, y, t) => store.handleTextTap(box.id, x, y, t)}
              onTapCancel={store.cancelTextTap}
              onTextChange={text => store.textStore.updateText(box.id, text)}
              onBlur={() => store.handleTextBlur(box.id)}
              onMove={(lng, lat) => store.handleTextMove(box.id, lng, lat)}
              onResize={width => store.textStore.setBoxWidth(box.id, width)}
              onRotate={deg => store.textStore.setRotation(box.id, deg)}
            />
          )
        })}
      </div>
    )
    return store.map ? createPortal(layer, store.map.getContainer()) : null
  }

  return (
    <>
      {renderTextBoxes()}
      {/* Coaching tip below a polygon/polyline: a 2nd click promotes it to
        vertex edit. Retires for good once the user does it (handleEnterVertexEdit). */}
      {store.showVertexEditHint &&
        store.selection?.vertexEditHint &&
        store.vertexHintScreen &&
        !store.interacting && (
          <div
            className="tw:animate-in tw:fade-in-0 tw:zoom-in-95 tw:pointer-events-none tw:absolute tw:z-20 tw:flex tw:w-max tw:items-center tw:gap-1.5 tw:rounded-md tw:bg-foreground tw:px-3 tw:py-1.5 tw:text-xs tw:text-background tw:shadow-md tw:duration-150"
            style={{
              left: store.vertexHintScreen.x,
              top: store.vertexHintScreen.y,
              transform: 'translate(-50%, 14px)',
            }}
          >
            <PencilIcon
              className="tw:size-3.5 tw:shrink-0"
              aria-hidden="true"
            />
            {Localization.translator.clickAgainToEditPoints}
          </div>
        )}
      {/* Mobile coaching tip shown on entering vertex-edit: touch has no right-click,
        so long-press a point to remove it. Retires after the first delete. */}
      {store.isMobileEnv &&
        store.showVertexDeleteHint &&
        store.vertexHintScreen &&
        !store.interacting && (
          <div
            className="tw:animate-in tw:fade-in-0 tw:zoom-in-95 tw:pointer-events-none tw:absolute tw:z-20 tw:flex tw:w-max tw:items-center tw:gap-1.5 tw:rounded-md tw:bg-foreground tw:px-3 tw:py-1.5 tw:text-xs tw:text-background tw:shadow-md tw:duration-150"
            style={{
              left: store.vertexHintScreen.x,
              top: store.vertexHintScreen.y,
              transform: 'translate(-50%, 14px)',
            }}
          >
            <Trash2Icon
              className="tw:size-3.5 tw:shrink-0"
              aria-hidden="true"
            />
            {Localization.translator.longPressToRemovePoint}
          </div>
        )}
      {/* Copy toolbar for a picked foreign (other-workflow) reference markup:
        one tap drops an editable duplicate into this workflow. */}
      {store.copySelectedId && store.foreignSelectedScreen && (
        <div
          className="tw:pointer-events-auto tw:absolute tw:z-20"
          style={{
            left: store.foreignSelectedScreen.x,
            top: store.foreignSelectedScreen.y,
            transform: `translate(-50%, calc(-100% - ${TOOLBAR_GAP}px))`,
          }}
        >
          <div className="tw:rounded-lg tw:bg-popover tw:p-1 tw:shadow-lg">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={store.copyForeignNow}
              className="tw:gap-1.5"
            >
              <CopyIcon aria-hidden="true" />
              {Localization.translator.copy}
            </Button>
          </div>
        </div>
      )}
      {/* Invisible hit-targets for resizing — the visible handles are drawn on
        the GL canvas (controller HANDLE layer) so they stay glued to the
        shape during move/resize; these just capture the drag (and, sitting
        above the canvas, keep Terra Draw from treating the grab as a move). */}
      {/* While an interaction is in flight the HTML chrome is hidden via
        visibility (NOT unmounted — the rotate handle holds the pointer
        capture that keeps the release off the canvas, and removing a
        captured element drops the capture → the release would read as a
        deselect click). Hidden elements also stop hit-testing, so the
        stale-positioned targets can't intercept the drag. */}
      {store.rectangleHandleScreens.map((handle, index) => (
        <div
          key={index}
          className={`tw:pointer-events-auto tw:absolute tw:size-4 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:cursor-nwse-resize tw:touch-none ${
            store.interacting ? 'tw:invisible' : ''
          }`}
          style={{ left: handle.x, top: handle.y }}
          onPointerDown={e => store.startRectangleResize(e, index)}
        />
      ))}
      {/* Path markups (line/polyline/polygon/arrow) + circle: a selection box
        so the rotate pin hangs off a visible frame (circle: its bounding box).
        Non-interactive; stays shown + tracking during a drag. Drawn BEFORE the
        corner handles so its outline doesn't paint over them. */}
      {store.selectionBoxScreens.length === 4 && (
        <svg className="tw:pointer-events-none tw:absolute tw:inset-0 tw:size-full">
          <polygon
            points={store.selectionBoxScreens
              .map(c => `${c.x},${c.y}`)
              .join(' ')}
            fill="none"
            strokeWidth={1}
            className="tw:stroke-primary"
          />
        </svg>
      )}
      {store.circleHandleScreens.map((handle, index) => (
        <div
          key={index}
          className={`tw:pointer-events-auto tw:absolute tw:-translate-x-1/2 tw:-translate-y-1/2 ${RESIZE_HANDLE_CLASS} ${
            index === 1 || index === 3
              ? 'tw:cursor-nesw-resize'
              : 'tw:cursor-nwse-resize'
          }`}
          style={{ left: handle.x, top: handle.y }}
          onPointerDown={e => store.startCircleResize(e, index)}
        />
      ))}
      {/* Freehand: a screen-space bounding box + 4 circle corner handles that
        stretch-scale the stroke (HTML, recomputed each render; stays shown
        during a drag). */}
      {store.freehandHandleScreens.length === 4 && (
        <svg className="tw:pointer-events-none tw:absolute tw:inset-0 tw:size-full">
          <polygon
            points={store.freehandHandleScreens
              .map(c => `${c.x},${c.y}`)
              .join(' ')}
            fill="none"
            strokeWidth={1}
            className="tw:stroke-primary"
          />
        </svg>
      )}
      {store.freehandHandleScreens.map((handle, index) => (
        <div
          key={index}
          className={`tw:pointer-events-auto tw:absolute tw:-translate-x-1/2 tw:-translate-y-1/2 ${RESIZE_HANDLE_CLASS} ${
            index === 1 || index === 3
              ? 'tw:cursor-nesw-resize'
              : 'tw:cursor-nwse-resize'
          }`}
          style={{ left: handle.x, top: handle.y }}
          onPointerDown={e => store.startFreehandResize(e, index)}
        />
      ))}
      {/* Rotate handle: a ⟳ pin hanging off the selection's bbox by a stem
        (so it reads as "turn this object", distinct from vertex circles);
        drag spins the markup around its centre (Shift snaps to 15°). */}
      {store.rotateHandleScreen && store.rotateStemScreen && (
        <svg
          className={`tw:pointer-events-none tw:absolute tw:inset-0 tw:size-full ${
            store.interacting ? 'tw:invisible' : ''
          }`}
        >
          <line
            x1={store.rotateStemScreen.x}
            y1={store.rotateStemScreen.y}
            x2={store.rotateHandleScreen.x}
            y2={store.rotateHandleScreen.y}
            strokeWidth={1.5}
            className="tw:stroke-primary"
          />
        </svg>
      )}
      {store.rotateHandleScreen && (
        <div
          className={`tw:pointer-events-auto tw:absolute tw:-translate-x-1/2 tw:-translate-y-1/2 tw:cursor-grab tw:active:cursor-grabbing ${ROTATE_HANDLE_CLASS} ${
            store.interacting ? 'tw:invisible' : ''
          }`}
          style={{
            left: store.rotateHandleScreen.x,
            top: store.rotateHandleScreen.y,
          }}
          onPointerDown={store.startRotate}
        >
          <RotateCwIcon className="tw:size-2.5 tw:text-primary" />
        </div>
      )}

      {store.activeTool === 'text' && (
        <div
          className="tw:pointer-events-auto tw:absolute tw:inset-0 tw:cursor-text"
          // Touch: pointerdown — its trailing click drops when the map shifts under the finger.
          // Mouse: click — the gesture's last event, so nothing later blurs the fresh box.
          onPointerDown={e => {
            if (e.pointerType !== 'touch') {
              // A dropped trailing click leaves the latch set; a mouse/pen press starts
              // a fresh gesture whose click must place (hybrid touch+trackpad devices).
              placedOnTouchRef.current = false
              return
            }
            placedOnTouchRef.current = true
            store.handleTextPlaceClick(e)
          }}
          onClick={e => {
            if (placedOnTouchRef.current) {
              placedOnTouchRef.current = false
              return
            }
            store.handleTextPlaceClick(e)
          }}
        />
      )}
    </>
  )
})

export default MarkupSelectionChrome
